import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Tooltip,
  IconButton,
  styled,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const PREFIX = "PurchaseGeolocMapList";

const classes = {
  purchaseGeolocListContainer: `${PREFIX}-purchaseGeolocListContainer`,
};

const PurchaseGeolocMapListContainer = styled("div")(({ theme }) => ({
  [`& .${classes.purchaseGeolocListContainer}`]: {
    overflowY: "auto",
    width: "450px",
    marginBottom: "20px",
    height: "400px",
    [theme.breakpoints.down("md")]: {
      marginLeft: "0",
      height: "auto",
      width: "100dvw",
      maxHeight: "450px",
      marginBottom: "0px",
    },
    "&::-webkit-scrollbar": {
      width: "7px",
      height: "7px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
  },
}));

const PurchaseGeolocMapList = ({
  purchasedGeolocs,
  showAll,
  setShowAll,
  handlePositionFocus,
  width,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");

  const filteredGeolocs = purchasedGeolocs.filter((item) => {
    const isExpired = dayjs(item.endDate).isBefore(dayjs());
    if (filter === "active") return !isExpired;
    if (filter === "inactive") return isExpired;
    return true;
  });

  return (
    <PurchaseGeolocMapListContainer>
      <Box sx={{ marginLeft: width < 769 ? "0" : "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            marginBottom: "10px",
          }}
        >
          <Button
            variant={filter === "all" ? "contained" : "outlined"}
            onClick={() => setFilter("all")}
            sx={{ width: "100px", height: "40px" }}
          >
            {t("myExpariences.geolocMapList.all")}
          </Button>
          <Button
            variant={filter === "active" ? "contained" : "outlined"}
            onClick={() => setFilter("active")}
            sx={{ marginLeft: "10px", width: "100px", height: "40px" }}
          >
            {t("myExpariences.geolocMapList.active")}
          </Button>
          <Button
            variant={filter === "inactive" ? "contained" : "outlined"}
            onClick={() => setFilter("inactive")}
            sx={{ marginLeft: "10px", width: "100px", height: "40px" }}
          >
            {t("myExpariences.geolocMapList.inactive")}
          </Button>
        </Box>
        <Paper elevation={3} className={classes.purchaseGeolocListContainer}>
          <Box sx={{ height: "100%", backgroundColor: "transparent" }}>
            <List>
              {filteredGeolocs.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ textAlign: "center" }}>
                        {t("myExpariences.geolocMapList.noGeolocs")}
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                filteredGeolocs
                  .slice(0, showAll ? filteredGeolocs.length : 3)
                  .map((item, index) => {
                    const isExpired = dayjs(item.endDate).isBefore(dayjs());
                    return (
                      <React.Fragment key={`purchaseGeoloc-${index}`}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Paper
                                elevation={1}
                                sx={{
                                  padding: "10px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  boxShadow: isExpired
                                    ? "0 0 10px #F08080"
                                    : "0 0 10px #77DD77",
                                  transition: "box-shadow 0.3s ease",
                                }}
                              >
                                <Box sx={{ width: "80%" }}>
                                  <Typography
                                    variant="body1"
                                    noWrap={false}
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: "100%",
                                      marginBottom: "10px",
                                      fontWeight: "bold",
                                      display: "-webkit-box",
                                      WebkitBoxOrient: "vertical",
                                      WebkitLineClamp: 2,
                                    }}
                                  >
                                    {item.title}
                                  </Typography>
                                  <Box>
                                    <Typography variant="body2">
                                      {`${t(
                                        "myExpariences.purchaseGeoloc.startDate"
                                      )} ${dayjs(item.startDate).format(
                                        "DD-MM-YYYY"
                                      )} ${t(
                                        "myExpariences.purchaseGeoloc.endDate"
                                      )} ${dayjs(item.endDate).format(
                                        "DD-MM-YYYY"
                                      )}`}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Tooltip
                                    title={t("purchaseGeoloc.goToThisLocation")}
                                  >
                                    <IconButton
                                      aria-label={t(
                                        "purchaseGeoloc.goToThisLocation"
                                      )}
                                      onClick={() => {
                                        handlePositionFocus(index, {
                                          latitude:
                                            item.position.coordinates[1],
                                          longitude:
                                            item.position.coordinates[0],
                                        });
                                      }}
                                    >
                                      <MyLocationIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isExpired ? "#F08080" : "#77DD77",
                                    }}
                                  >
                                    {isExpired
                                      ? t(
                                          "myExpariences.purchaseGeoloc.inactive"
                                        )
                                      : t(
                                          "myExpariences.purchaseGeoloc.active"
                                        )}
                                  </Typography>
                                </Box>
                              </Paper>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })
              )}
            </List>
            {width < 769 && filteredGeolocs.length > 3 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "10px 0",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll
                    ? t("myExpariences.geolocMapList.seeLess")
                    : t("myExpariences.geolocMapList.seeMore")}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </PurchaseGeolocMapListContainer>
  );
};

export default PurchaseGeolocMapList;
